import React from 'react'

import type { ResponsiveValue } from '@gousto-internal/citrus-react'
import {
  AlignItems,
  BorderStyle,
  Box,
  Color,
  Display,
  FontWeight,
  Space,
  Text,
  TextAlign,
} from '@gousto-internal/citrus-react'

import { getPromotionalPricing } from '../helpers/discount/discounts'
import type { BoxComparisonTableHeaderProps, BoxComparisonTableProps } from './boxPriceBlockModels'

const colorSchemes = {
  selectMoreRecipes: {
    boxType: Box,
    bgColor: Color.ColdGrey_700,
    color: Color.White,
    testingId: 'more-recipes',
    borderColor: Color.ColdGrey_100,
    fontWeight: FontWeight.Bold,
  },
  bestPrice: {
    boxType: Box,
    bgColor: Color.Success_800,
    color: Color.White,
    testingId: 'best-price',
    borderColor: Color.ColdGrey_200,
    fontWeight: FontWeight.Bold,
  },
}

export const BoxComparisonTableHeader = ({ type, headerValue }: BoxComparisonTableHeaderProps) => {
  const {
    boxType: HeaderBox,
    color,
    bgColor,
    borderColor,
    testingId,
    fontWeight,
  } = colorSchemes[type]

  return (
    <HeaderBox
      bg={bgColor as ResponsiveValue<Color>}
      borderStyle={BorderStyle.Solid}
      borderColor={borderColor}
      borderWidth={0.5}
      color={color}
      paddingV={1}
      data-testid={testingId}
    >
      <Text textAlign={TextAlign.Center} size={1} fontWeight={fontWeight}>
        {headerValue}
      </Text>
    </HeaderBox>
  )
}

export const BoxComparisonTable = ({
  pricePerPortion,
  pricePerBox,
  boxSize,
  numPortions,
}: BoxComparisonTableProps) => {
  const normalBoxPrice = Number(pricePerBox)

  const promotionalPricing = getPromotionalPricing(boxSize, numPortions, normalBoxPrice)

  const {
    firstBoxPriceWithDiscount: firstBoxDiscountedPrice,
    firstBoxPerPortionPriceWithDiscount: discountedPricePerPortion,
    totalSaving: totalPromotionalSavings,
  } = promotionalPricing

  const totalSavings: string = totalPromotionalSavings
  const testId = 'total-promotional-savings'

  return (
    <>
      <Box data-no-code="boxPricesPriceSummary" width="100%" display={Display.InlineFlex}>
        <Box
          width="50%"
          bg={Color.NeutralGrey_50}
          borderStyle={BorderStyle.Solid}
          borderColor={Color.ColdGrey_200}
          borderWidth={0.5}
          borderTopWidth={0}
          alignItems={AlignItems.Center}
        >
          <Space size={2} />
          <Text
            size={7}
            textAlign={TextAlign.Center}
            color={Color.Success_800}
            fontWeight={FontWeight.Bold}
            data-no-code="boxPricesPricePerOrder"
          >
            £{firstBoxDiscountedPrice}{' '}
            <Text size={2} textAlign={TextAlign.Center} display={Display.Inline}>
              per order
            </Text>
          </Text>
          <Space size={1} />
          <Text
            size={2}
            textAlign={TextAlign.Center}
            color={Color.ColdGrey_600}
            data-no-code="boxPricesPricePerOrderOriginal"
          >
            Usually £{pricePerBox} per order
          </Text>

          <Space size={2} />
        </Box>
        <Box
          width="50%"
          borderStyle={BorderStyle.Solid}
          borderColor={Color.ColdGrey_200}
          borderRightWidth={0.5}
          borderBottomWidth={0.5}
          alignItems={AlignItems.Center}
        >
          <Space size={2} />
          <Text
            size={7}
            textAlign={TextAlign.Center}
            color={Color.Success_800}
            fontWeight={FontWeight.Bold}
            data-no-code="boxPricesPricePerPortion"
          >
            £{discountedPricePerPortion}{' '}
            <Text size={2} textAlign={TextAlign.Center} display={Display.Inline}>
              per portion
            </Text>
          </Text>
          <Space size={1} />
          <Text
            size={2}
            textAlign={TextAlign.Center}
            color={Color.ColdGrey_600}
            data-no-code="boxPricesPricePerPortionOriginal"
          >
            Usually £{pricePerPortion} per portion
          </Text>
          <Space size={2} />
        </Box>
      </Box>

      <Space size={4} />

      <Text data-testid={testId} size={4} fontWeight={FontWeight.Bold} textAlign={TextAlign.Center}>
        Save up to{' '}
        <Text
          color={Color.Success_800}
          size={4}
          fontWeight={FontWeight.Bold}
          textAlign={TextAlign.Center}
          display={Display.Inline}
        >
          £{totalSavings}
        </Text>{' '}
        in your first 2 months
      </Text>
    </>
  )
}
